/**
 * useBuildingDueDiligence Hook
 *
 * Custom hook for fetching building due diligence dot values
 * Implements stale-while-revalidate pattern for optimal performance
 */

import { useState, useEffect, useRef } from "react";
import { useSession } from "@clerk/clerk-react";
import { BuildingDTO } from "shared/types/building";
import {
  dueDiligenceCache,
  DotValues,
} from "shared/services/dueDiligenceCacheService";
import {
  dueDiligenceQueue,
  mergeDotValues,
} from "shared/services/dueDiligenceQueueManager";
import { createTooltipContent } from "components/card/buildingCard";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";

interface UseBuildingDueDiligenceOptions {
  /**
   * Maximum number of units to process (for performance)
   * Default: undefined (process all units)
   */
  maxUnits?: number;

  /**
   * Whether to process units at all
   * Default: true
   */
  includeUnits?: boolean;

  /**
   * Whether to use stale cache while revalidating
   * Default: true
   */
  allowStale?: boolean;

  /**
   * Skip calculation entirely (for lazy loading)
   * Default: false
   */
  skip?: boolean;
}

interface UseBuildingDueDiligenceReturn {
  /**
   * The calculated dot values (may be stale)
   */
  dotValues: DotValues | null;

  /**
   * Whether data is currently loading
   */
  isLoading: boolean;

  /**
   * Whether the displayed data is stale and being revalidated
   */
  isRevalidating: boolean;

  /**
   * Any error that occurred
   */
  error: Error | null;

  /**
   * Manually trigger a refresh
   */
  refresh: () => void;

  /**
   * Clear the cache for this building
   */
  clearCache: () => void;
}

// Building categories to calculate
const BUILDING_CATEGORIES = [
  'Elektriciteit',
  'Brandveiligheid',
  'Waterhuishouding',
  'Energieprestaties',
  'Stedenbouw',
  'Kadaster',
  'Verzekeringen',
  'Bodem',
  'Lift',
  'Gevaarlijke stoffen',
  'Stabiliteit',
  'Plannen',
  'Vis. inspectie',
  'Eigendom',
  'Klimatisatie',
  'Taxatie',
  'Erfdienstbaarheden',
];

// Unit categories to calculate
const UNIT_CATEGORIES = [
  'Elektriciteit',
  'Brandveiligheid',
  'Waterhuishouding',
  'Energieprestaties',
  'Gevaarlijke stoffen',
  'Woonconformiteit',
  'Vis. inspectie',
  'Toestellen',
  'Klimatisatie',
];

export const useBuildingDueDiligence = (
  building: BuildingDTO,
  options: UseBuildingDueDiligenceOptions = {}
): UseBuildingDueDiligenceReturn => {
  const {
    maxUnits,
    includeUnits = true,
    allowStale = true,
    skip = false,
  } = options;

  const { session } = useSession();
  const [dotValues, setDotValues] = useState<DotValues | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const buildingIdRef = useRef<string | null>(null);

  /**
   * Prepare dot values for display (deduplicate, create tooltips)
   */
  const prepareForDisplay = (values: DotValues): DotValues => {
    const prepared = { ...values };

    Object.keys(prepared).forEach(color => {
      const key = color as keyof DotValues;
      // Deduplicate labels
      prepared[key].labels = [...new Set(prepared[key].labels)];
      // Precompute tooltip content
      prepared[key].tooltipContent = createTooltipContent(prepared[key].sources);
    });

    return prepared;
  };

  /**
   * Calculate dot values for the building
   */
  const calculateDotValues = async (
    isRevalidation = false
  ): Promise<void> => {
    if (!session) {
      setIsLoading(false);
      return;
    }

    try {
      // Set loading/revalidating state
      if (isRevalidation) {
        setIsRevalidating(true);
      } else {
        setIsLoading(true);
      }

      // Create abort controller for this calculation
      abortControllerRef.current = new AbortController();

      // Check if we need to fetch complete building data
      let completeBuilding = building;
      const needsCompleteBuilding = typeof building.units === 'number' ||
        !building.electricityInfo ||
        !building.energyPerformanceInfo;

      if (needsCompleteBuilding) {
        console.log("[useBuildingDueDiligence] Fetching complete building data for:", building.id);

        try {
          const response = await fetchWithAuth(
            `${API_URL}/building/${building.id}`,
            {
              method: "GET",
              signal: abortControllerRef.current.signal,
            },
            session
          );

          if (response.ok) {
            completeBuilding = await response.json();
            console.log("[useBuildingDueDiligence] Successfully fetched complete building data");
          } else {
            console.error("[useBuildingDueDiligence] Failed to fetch complete building:", response.status);
          }
        } catch (error: any) {
          if (error.name !== 'AbortError') {
            console.error("[useBuildingDueDiligence] Error fetching complete building:", error);
          }
        }
      }

      // Calculate building categories
      const buildingResults = await dueDiligenceQueue.calculateBuilding(
        building.id!,
        completeBuilding,
        BUILDING_CATEGORIES,
        session,
        abortControllerRef.current
      );

      // Merge building results
      let totalValues = mergeDotValues(Array.from(buildingResults.values()));

      // Add building sources
      buildingResults.forEach((dotValue, category) => {
        Object.keys(dotValue).forEach(color => {
          const key = color as keyof DotValues;
          dotValue[key].labels.forEach((label: string) => {
            totalValues[key].sources.push({
              id: building.id!,
              name: "Gebouw algemeen",
              label: `${category}: ${label}`,
              isBuilding: true,
            });
          });
        });
      });

      // Process units if enabled
      if (includeUnits && completeBuilding.units) {
        let unitsList: any[] = [];

        // Handle both array and count formats
        if (Array.isArray(completeBuilding.units)) {
          unitsList = maxUnits ? completeBuilding.units.slice(0, maxUnits) : completeBuilding.units;
        }

        // Check if units have detailed data, fetch if needed
        if (unitsList.length > 0) {
          const sampleUnit = unitsList[0];
          const hasDetailedUnitData = !!(
            sampleUnit.electricityInfo ||
            sampleUnit.energyPerformanceInfo ||
            sampleUnit.fireSecurityUnitInfo
          );

          if (!hasDetailedUnitData) {
            console.log("[useBuildingDueDiligence] Fetching detailed unit data...");

            // Fetch detailed data for each unit
            const detailedUnits = await Promise.all(
              unitsList.map(async (unit) => {
                try {
                  const response = await fetchWithAuth(
                    `${API_URL}/unit/${unit.id}`,
                    {
                      method: "GET",
                      signal: abortControllerRef.current?.signal,
                    },
                    session
                  );

                  if (response.ok) {
                    return await response.json();
                  }
                } catch (error: any) {
                  if (error.name !== 'AbortError') {
                    console.error(`[useBuildingDueDiligence] Error fetching unit ${unit.id}:`, error);
                  }
                }
                return unit; // Return original if fetch fails
              })
            );

            unitsList = detailedUnits;
            console.log("[useBuildingDueDiligence] Fetched detailed data for", unitsList.length, "units");
          }
        }

        // Process units in batches
        const BATCH_SIZE = 3;
        for (let i = 0; i < unitsList.length; i += BATCH_SIZE) {
          // Check for abort
          if (abortControllerRef.current?.signal.aborted) {
            throw new Error('Calculation aborted');
          }

          const unitBatch = unitsList.slice(i, i + BATCH_SIZE);

          // Calculate all units in batch concurrently
          const unitPromises = unitBatch.map(unit =>
            dueDiligenceQueue.calculateUnit(
              unit.id,
              unit,
              UNIT_CATEGORIES,
              session,
              abortControllerRef.current!
            )
          );

          const unitBatchResults = await Promise.all(unitPromises);

          // Merge unit results into total
          unitBatchResults.forEach((unitResult, idx) => {
            const unit = unitBatch[idx];

            unitResult.forEach((dotValue, category) => {
              Object.keys(dotValue).forEach(color => {
                const key = color as keyof DotValues;

                totalValues[key].count += dotValue[key].count;
                totalValues[key].labels.push(...dotValue[key].labels);

                dotValue[key].labels.forEach((label: string) => {
                  totalValues[key].sources.push({
                    id: unit.id,
                    name: unit.unitName || `Unit ${unit.id}`,
                    label: `${category}: ${label}`,
                    isUnit: true,
                    unitType: unit.unitType,
                  });
                });
              });
            });
          });
        }
      }

      // Prepare for display
      const finalValues = prepareForDisplay(totalValues);

      // Save to cache
      dueDiligenceCache.setBuildingDotValues(building.id!, finalValues);

      // Update state
      setDotValues(finalValues);
      setError(null);
    } catch (err: any) {
      if (err.message !== 'Calculation aborted' && err.message !== 'Task aborted') {
        console.error('[useBuildingDueDiligence] Error calculating dot values:', err);
        setError(err);
      }
    } finally {
      setIsLoading(false);
      setIsRevalidating(false);
      abortControllerRef.current = null;
    }
  };

  /**
   * Main effect - load from cache or calculate
   */
  useEffect(() => {
    // Skip if requested (for lazy loading)
    if (skip) {
      setIsLoading(false);
      return;
    }

    // Building ID changed - cleanup previous calculation
    if (buildingIdRef.current !== building.id) {
      if (buildingIdRef.current) {
        dueDiligenceQueue.cancelTasks(buildingIdRef.current);
      }
      buildingIdRef.current = building.id!;
    }

    // Try to load from cache first
    const cached = dueDiligenceCache.getBuildingDotValues(building.id!, allowStale);

    if (cached) {
      // We have cached data - use it immediately
      setDotValues(cached);
      setIsLoading(false);

      // Check if it's stale and should be revalidated
      if (allowStale && dueDiligenceCache.isBuildingStale(building.id!)) {
        // Revalidate in background
        calculateDotValues(true);
      }
    } else {
      // No cache - fetch fresh data
      calculateDotValues(false);
    }

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [building.id, session, skip]);

  /**
   * Manual refresh
   */
  const refresh = () => {
    calculateDotValues(false);
  };

  /**
   * Clear cache for this building
   */
  const clearCache = () => {
    const unitIds = Array.isArray(building.units)
      ? building.units.map(u => u.id)
      : undefined;

    dueDiligenceCache.invalidateBuilding(building.id!, unitIds as string[]);
    calculateDotValues(false);
  };

  return {
    dotValues,
    isLoading,
    isRevalidating,
    error,
    refresh,
    clearCache,
  };
};
